// Cierre por solicitante, retrabajo y tope de tickets sin cerrar (CC-CONSOLE #1337) — la
// parte decidible, sin axios ni firebase para poder probarla en un worktree.
//
// Es el espejo de `serviceDeskClosure.js` del backend (resolveClosePermission).
// El backend sigue siendo la autoridad: esto sólo sirve para decirle a quien
// arrastra una tarjeta a «Cerrado» por qué no puede ANTES de la llamada, en vez
// de moverla, esperar el 403 y devolverla. Si las dos copias llegan a
// discrepar, gana el 403 (que trae su propio mensaje en `meta.message`).

export const PENDING_CLOSURE_STATUS = "resolved";
export const CLOSED_STATUS = "closed";
export const REWORK_STATUS = "rework";

/** Códigos estables que el backend manda en `meta.messageKey`. */
export const PENDING_CLOSURE_LIMIT_KEY = "SD_PENDING_CLOSURE_LIMIT";
export const CLOSE_FORBIDDEN_KEY = "SD_CLOSE_FORBIDDEN";

/** Mismo mínimo que `sanitizeReworkReason` del backend (que además recorta). */
export const MIN_REWORK_REASON_LENGTH = 5;

/**
 * Respaldo para pintar «Rehacer» donde el estado no viene de
 * `settings.statuses` (vistas de cliente) o la mesa guardó ajustes anteriores
 * a CC-CONSOLE #1337 y todavía no lo trae con color.
 */
export const REWORK_META = { label: "Rehacer", color: "#F97316" } as const;

export type CloseReason = "requester" | "assignee" | "not_resolved" | "locked" | "not_assignee";

export interface ClosePermission {
  allowed: boolean;
  reason: CloseReason;
  /** Epoch ms desde el que puede cerrarlo la persona asignada; null si no aplica. */
  unlockAt: number | null;
}

/** Lo mínimo de un ticket (vista de agente) que hace falta para decidir. */
export interface ClosureTicketLike {
  status: string;
  requester?: { userId?: string | null; email?: string | null } | null;
  assignedAgentId?: string | null;
  assignedDevId?: string | null;
  closure?: { unlocked?: boolean; unlockAt?: number | null } | null;
}

export interface ClosureActor {
  id?: string | null;
  email?: string | null;
}

const normalizeEmail = (v: unknown): string => String(v ?? "").trim().toLowerCase();

/** Quien abrió el ticket: por cuenta o, si se abrió sin sesión, por correo. */
export function isTicketRequester(ticket: ClosureTicketLike, me: ClosureActor): boolean {
  const requester = ticket.requester || {};
  if (me.id && requester.userId && String(requester.userId) === String(me.id)) return true;
  const email = normalizeEmail(me.email);
  return Boolean(email) && normalizeEmail(requester.email) === email;
}

export function isTicketAssignee(ticket: ClosureTicketLike, me: ClosureActor): boolean {
  const id = me.id ? String(me.id) : "";
  if (!id) return false;
  return [ticket.assignedAgentId, ticket.assignedDevId].some((x) => Boolean(x) && String(x) === id);
}

/**
 * ¿Puede `me` cerrar `ticket`? Mismo orden de preguntas que el backend, para
 * que el motivo coincida con el del 403.
 *
 * `closure.unlocked` se calculó cuando se cargó la lista; un tablero puede
 * llevar horas abierto, así que también se da por desbloqueado si `unlockAt`
 * ya pasó según `now`. Si el reloj del navegador va adelantado, lo peor que
 * ocurre es una llamada que el backend rechaza con su mensaje.
 */
export function canCloseTicket(
  ticket: ClosureTicketLike,
  me: ClosureActor,
  now: number = Date.now(),
): ClosePermission {
  const unlockAt = typeof ticket.closure?.unlockAt === "number" ? ticket.closure.unlockAt : null;
  if (isTicketRequester(ticket, me)) return { allowed: true, reason: "requester", unlockAt };
  if (ticket.status !== PENDING_CLOSURE_STATUS) return { allowed: false, reason: "not_resolved", unlockAt: null };
  const unlocked = ticket.closure?.unlocked === true || (unlockAt != null && now >= unlockAt);
  if (!unlocked) return { allowed: false, reason: "locked", unlockAt };
  if (!isTicketAssignee(ticket, me)) return { allowed: false, reason: "not_assignee", unlockAt };
  return { allowed: true, reason: "assignee", unlockAt };
}

/**
 * Duración legible a dos unidades: "45 min", "3 h 5 min", "3 d 4 h". Los
 * tiempos de CC-CONSOLE #1337 (espera al cliente, Resuelto → Cerrado) van de minutos a
 * semanas; la tercera unidad sólo mete ruido en una tarjeta.
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "< 1 min";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h} h ${m} min` : `${h} h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d} d ${rh} h` : `${d} d`;
}

interface ApiErrorLike {
  response?: { status?: number; data?: { message?: unknown; meta?: { messageKey?: unknown; message?: unknown } } };
}

/**
 * El mensaje que el backend manda listo para mostrar (`meta.message`); "" si
 * el error no trae uno, para que quien llama ponga su texto de respaldo.
 */
export function apiErrorMessage(err: unknown): string {
  const data = (err as ApiErrorLike)?.response?.data;
  if (typeof data?.meta?.message === "string" && data.meta.message) return data.meta.message;
  if (typeof data?.message === "string" && data.message) return data.message;
  return "";
}

const errorKey = (err: unknown): unknown => (err as ApiErrorLike)?.response?.data?.meta?.messageKey;
const errorStatus = (err: unknown): unknown => (err as ApiErrorLike)?.response?.status;

/** El alta se rechazó por tener 3+ tickets resueltos sin cerrar. */
export function isPendingClosureLimitError(err: unknown): boolean {
  return errorStatus(err) === 409 && errorKey(err) === PENDING_CLOSURE_LIMIT_KEY;
}

/** El cambio a «Cerrado» se rechazó: no es el solicitante ni el asignado con el ticket desbloqueado. */
export function isCloseForbiddenError(err: unknown): boolean {
  return errorStatus(err) === 403 && errorKey(err) === CLOSE_FORBIDDEN_KEY;
}

export function isValidReworkReason(reason: string): boolean {
  return reason.trim().length >= MIN_REWORK_REASON_LENGTH;
}

/**
 * ¿Se muestra el aviso «cierra tus tickets resueltos»? Siempre que esté
 * bloqueado; y también cuando llega redirigido desde el formulario
 * (`?pendientes=1`) y todavía le queda alguno — entre el redirect y la carga
 * pudo cerrar uno en otra pestaña y bajar de 3, y un aviso vacío confunde.
 */
export function shouldShowPendingClosureAlert(
  pending: { blocked?: boolean; count?: number } | null | undefined,
  redirected: boolean,
): boolean {
  if (!pending) return false;
  if (pending.blocked) return true;
  return redirected && (pending.count ?? 0) > 0;
}

/** Etiqueta y color de respaldo de un estado; null si no hay nada mejor que lo que ya se tenga. */
export function statusFallback(status: string | null | undefined): { label: string; color: string } | null {
  return status === REWORK_STATUS ? REWORK_META : null;
}

/** Día (sin hora) en que se desbloquea el cierre: "12 de octubre de 2026". */
export function formatClosureDate(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
}
